import { useCallback, useMemo, useRef, useState } from 'react';
import type { FormRendererRef } from '@locus-taxy/encore-form';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { useUserProfile } from './useUserProfile';
import { usePhotoUrl } from '../../hooks/usePhotoUrl';
import { useFileUpload } from '../../api/endpoints/files/useFileUpload';
import { useLogoutWithO11y } from '../../hooks/useLogoutWithO11y';
import { buildDateOfBirthPayload } from './profileFormSchemas';
import type { ProfileViewMode } from './profileTypes';
import { getGenderOptions } from './profileConstants';
import {
  useCurrentUser,
  type DocumentInfo,
} from '../../api/endpoints/useCurrentUser';

/**
 * The record-access dimension's display label, derived from its `name` — the BE
 * transmits no label (HLD v0.3.1 §A). Backend-driven data, so deliberately not
 * `t()`-wrapped, matching `RecordAccessSelector`.
 */
const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

/**
 * The resolved-names sibling of the dimension's tag field: `recordTagField`
 * (`teamIds` / `nodeIds`) carries the ids, while the profile renders the display
 * names the BE denormalizes alongside them (`teams` / `nodes`). `/me` types only
 * the id field, so the names key is *derived* from it rather than looked up.
 *
 * For the `team` dimension this resolves to exactly the historical `teams`, so FR
 * behaviour is bit-identical. A dimension whose tag field does not end in `Ids`
 * yields a key that misses, which renders the row blank — the same thing that
 * happens today for any non-team dimension, never worse.
 */
function namesFieldFor(recordTagField: string): string {
  return recordTagField.replace(/Ids$/, 's');
}

/** The `teams`/`teamIds` pair the FR deployment used before dimensions existed. */
const LEGACY_TAG_FIELD = 'teamIds';

/**
 * Tri-state pending photo:
 *   undefined → unchanged (preserve existing photo)
 *   DocumentInfo → pending upload (replace photo on Save)
 *   null → pending removal (clear photo on Save)
 */
type PendingPhoto = DocumentInfo | null | undefined;

/** Resolve which fileId drives the avatar preview given pending and persisted state. */
function resolvePreviewFileId(
  pendingPhoto: PendingPhoto,
  persistedFileId: string | null | undefined
): string | null {
  if (pendingPhoto === undefined) {
    return persistedFileId ?? null;
  }
  if (pendingPhoto === null) {
    return null;
  }
  return pendingPhoto.fileId ?? null;
}

/** Build the save payload from form data, falling back to existing entity data. */
function buildSavePayload(
  personalInfoFormData: Record<string, unknown>,
  localeFormData: Record<string, unknown>,
  entityData: Record<string, unknown> | undefined,
  configuredTimezone: string,
  configuredLocale: string,
  pendingPhoto: PendingPhoto
) {
  const existingProfileInfo = entityData?.profileInformation as
    | Record<string, unknown>
    | undefined;
  const existingPrefs = entityData?.preferences as
    | Record<string, unknown>
    | undefined;

  const personalProfile = (
    Object.keys(personalInfoFormData).length > 0
      ? personalInfoFormData.profileInformation
      : existingProfileInfo
  ) as Record<string, unknown> | undefined;
  const localePrefs = (
    Object.keys(localeFormData).length > 0
      ? localeFormData.preferences
      : existingPrefs
  ) as Record<string, unknown> | undefined;

  const phoneNumber = (personalProfile?.phoneNumber as string) ?? undefined;
  const gender = (personalProfile?.gender as string) ?? undefined;
  const rawDateOfBirth =
    (personalProfile?.dateOfBirth as string | null | undefined) ?? null;

  const newTimezone = (localePrefs?.timezone as string) ?? configuredTimezone;
  const newLocale =
    (localePrefs?.localeIdentifier as string) ?? configuredLocale;

  const dateOfBirth = buildDateOfBirthPayload(rawDateOfBirth, newTimezone);

  const profileInformation: Record<string, unknown> = {
    phoneNumber,
    gender,
    dateOfBirth,
  };
  if (pendingPhoto !== undefined) {
    profileInformation.photo = pendingPhoto;
  }

  return {
    payload: {
      profileInformation,
      preferences: { timezone: newTimezone, localeIdentifier: newLocale },
    },
    newTimezone,
    newLocale,
  };
}

/** Merge the save payload with existing entity data, removing admin-managed fields. */
function buildMergedVariables(
  entityData: Record<string, unknown> | undefined,
  payload: {
    profileInformation: Record<string, unknown>;
    preferences: Record<string, unknown>;
  },
  recordTagField?: string
): Record<string, unknown> {
  const existingProfileInfo = entityData?.profileInformation as
    | Record<string, unknown>
    | undefined;
  const existingPrefs = entityData?.preferences as
    | Record<string, unknown>
    | undefined;

  const merged: Record<string, unknown> = {
    ...entityData,
    profileInformation: {
      ...existingProfileInfo,
      ...payload.profileInformation,
    },
    preferences: { ...existingPrefs, ...payload.preferences },
  };

  // Remove admin-managed fields that must NOT be in update payload
  delete merged.roleId;
  delete merged.teamIds;
  delete merged.skillIds;
  delete merged.role;
  delete merged.teams;
  delete merged.skills;

  // Record-access tags are admin-managed too, and on a node-based client they
  // are not called `teamIds`/`teams` (DSCO-5543). Strip whatever the active
  // dimension names them. The literals above stay as a superset so a `team`
  // deployment — and a client reporting no dimension at all — is unchanged.
  if (recordTagField) {
    delete merged[recordTagField];
    delete merged[namesFieldFor(recordTagField)];
  }

  return merged;
}

/**
 * useProfilePage — orchestration hook for the Profile Settings page.
 * Manages View/Edit toggle, form refs, dirty state, save/cancel flows,
 * photo upload, and the unsaved changes dialog.
 */
export function useProfilePage() {
  const { t } = useTranslation();
  const {
    fullName,
    emailAddress,
    photo,
    configuredTimezone,
    configuredLocale,
    timezoneOptions,
    localeOptions,
    canUpdate,
    clientId,
    personnel,
    entityDetails,
    updateUser,
    updateActionId,
    refetchEntityDetails,
  } = useUserProfile();

  // The record-access dimension drives both the Work Profile row's label and the
  // entityData key its value is read from — neither is a `team` literal any more.
  const { activeDimension } = useCurrentUser(clientId);

  // --- Mode & state ---
  const [mode, setMode] = useState<ProfileViewMode>('view');
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // --- Form refs ---
  const personalInfoFormRef = useRef<FormRendererRef>(null);
  const localeFormRef = useRef<FormRendererRef>(null);

  // --- Photo handling (pending state deferred until Save) ---
  const [pendingPhoto, setPendingPhoto] = useState<PendingPhoto>(undefined);

  const previewFileId = resolvePreviewFileId(pendingPhoto, photo?.fileId);

  const { photoUrl, loading: photoLoading } = usePhotoUrl({
    fileId: previewFileId,
    clientId,
  });
  const { uploadFile, uploading: photoUploading } = useFileUpload(clientId);

  // --- Logout ---
  const logout = useLogoutWithO11y();

  // --- Form data tracking (for building save payload) ---
  const [personalInfoFormData, setPersonalInfoFormData] = useState<
    Record<string, unknown>
  >({});
  const [localeFormData, setLocaleFormData] = useState<Record<string, unknown>>(
    {}
  );

  // --- Entity data from profile ---
  const entityData = useMemo(
    () => personnel ?? entityDetails?.data?.data?.entityData ?? undefined,
    [personnel, entityDetails]
  );

  // --- Resolved work profile data ---
  const workProfileData = useMemo(() => {
    // No dimension (enforcement disabled, or `/me` not yet resolved) → keep the
    // historical "Teams" label and `teams` key, so FR is untouched either way.
    // Pluralized to match: disco-ui's `MultiSelectPopover` documents this same
    // derivation as "pass it in the plural" (see RecordAccessSelector), and an
    // unpluralized "Team" here would be a real FR regression from today's
    // "Teams" copy.
    const accessDimensionLabel = activeDimension
      ? `${capitalize(activeDimension.name)}s`
      : t('dashboard.userDetails.teams', 'Teams');
    const namesField = namesFieldFor(
      activeDimension?.recordTagField ?? LEGACY_TAG_FIELD
    );

    const data = entityData as Record<string, unknown> | undefined;
    if (!data) {
      return {
        roleName: '',
        accessDimensionLabel,
        accessDimensionNames: '',
        skillNames: '',
        joiningDate: null as { local: string; timeZone: string } | null,
      };
    }

    const roleName = (data.role as string) ?? '';
    const accessDimensionValues = data[namesField] as string[] | undefined;
    const skills = data.skills as string[] | undefined;
    const competency = data.competency as Record<string, unknown> | undefined;
    const joiningDate = competency?.joiningDate as
      | { local: string; timeZone: string }
      | null
      | undefined;

    return {
      roleName,
      accessDimensionLabel,
      accessDimensionNames: accessDimensionValues?.join(', ') ?? '',
      skillNames: skills?.join(', ') ?? '',
      joiningDate: joiningDate ?? null,
    };
  }, [entityData, activeDimension, t]);

  // --- View mode fields for PersonalInfoSection ---
  const personalInfoViewFields = useMemo(() => {
    const data = entityData as Record<string, unknown> | undefined;
    const profileInfo = data?.profileInformation as
      | Record<string, unknown>
      | undefined;
    const dob = profileInfo?.dateOfBirth as
      | { local?: string }
      | string
      | null
      | undefined;
    const dobDisplay = typeof dob === 'string' ? dob : (dob?.local ?? null);

    return [
      {
        label: t('dashboard.userDetails.fullName', 'Full Name'),
        value: (data?.name as string) ?? null,
      },
      {
        label: t('dashboard.userDetails.emailAddress', 'Email Address'),
        value: (profileInfo?.emailAddress as string) ?? null,
      },
      {
        label: t('dashboard.userDetails.phoneNumber', 'Phone Number'),
        value: (profileInfo?.phoneNumber as string) ?? null,
      },
      {
        label: t('dashboard.userDetails.employeeId', 'Employee ID'),
        value: (data?.id as string) ?? null,
      },
      {
        label: t('dashboard.userDetails.gender', 'Gender'),
        value:
          getGenderOptions(t).find(
            o => o.value === (profileInfo?.gender as string)
          )?.label ??
          (profileInfo?.gender as string) ??
          null,
      },
      {
        label: t('dashboard.userDetails.dateOfBirth', 'Date of Birth'),
        value: dobDisplay
          ? (() => {
              try {
                // Format as locale-aware date (e.g. "Jul 23, 1993")
                const [y, m, d] = dobDisplay.split('-').map(Number);
                return new Intl.DateTimeFormat(configuredLocale, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                }).format(new Date(y, m - 1, d));
              } catch {
                return dobDisplay;
              }
            })()
          : null,
      },
    ];
  }, [entityData, configuredLocale, t]);

  // --- Locale view fields ---
  const localeViewFields = useMemo(
    () => [
      {
        label: t('dashboard.userDetails.timezone', 'Timezone'),
        value: configuredTimezone,
      },
      {
        label: t('dashboard.userDetails.language', 'Language'),
        value: configuredLocale,
      },
    ],
    [configuredTimezone, configuredLocale, t]
  );

  // --- Last updated display ---
  const lastUpdated = useMemo(() => {
    const updatedAt = entityDetails?.data?.data?.updatedAt;
    if (!updatedAt) {
      return null;
    }
    try {
      return new Intl.DateTimeFormat(configuredLocale, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: configuredTimezone,
      }).format(new Date(updatedAt));
    } catch {
      return updatedAt;
    }
  }, [entityDetails, configuredLocale, configuredTimezone]);

  // --- Callbacks ---
  const handlePersonalInfoChange = useCallback(
    (data: Record<string, unknown>) => {
      setPersonalInfoFormData(data);
      setIsDirty(true);
    },
    []
  );

  const handleLocaleChange = useCallback((data: Record<string, unknown>) => {
    setLocaleFormData(data);
    setIsDirty(true);
  }, []);

  const handlePhotoSelect = useCallback(
    async (file: File) => {
      try {
        const documentInfo: DocumentInfo = await uploadFile(file);
        setPendingPhoto(documentInfo);
        setIsDirty(true);
      } catch {
        // Error already shown by uploadFile
      }
    },
    [uploadFile]
  );

  const handlePhotoRemove = useCallback(() => {
    setPendingPhoto(null);
    setIsDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    // 1. Validate both forms
    const personalValid = personalInfoFormRef.current?.validate();
    const localeValid = localeFormRef.current?.validate();
    if (!personalValid?.isValid || !localeValid?.isValid) {
      return;
    }

    // 2. Build payload and merge with existing entity data
    const existingData = entityData as Record<string, unknown> | undefined;
    const { payload, newTimezone, newLocale } = buildSavePayload(
      personalInfoFormData,
      localeFormData,
      existingData,
      configuredTimezone,
      configuredLocale,
      pendingPhoto
    );
    const mergedVariables = buildMergedVariables(
      existingData,
      payload,
      activeDimension?.recordTagField
    );

    // 3. Call mutation (useUpdateUser handles success toast automatically)
    setIsSaving(true);
    try {
      if (!updateActionId) {
        return;
      }

      await updateUser.mutateAsync({
        actionId: updateActionId,
        variables: mergedVariables,
      });

      await refetchEntityDetails();
      setMode('view');
      setIsDirty(false);
      setPendingPhoto(undefined);

      // 4. Reload if timezone or locale changed
      if (
        newTimezone !== configuredTimezone ||
        newLocale !== configuredLocale
      ) {
        window.location.reload();
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    personalInfoFormData,
    localeFormData,
    entityData,
    configuredTimezone,
    configuredLocale,
    updateActionId,
    updateUser,
    refetchEntityDetails,
    pendingPhoto,
    activeDimension,
  ]);

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setShowUnsavedDialog(true);
    } else {
      setMode('view');
    }
  }, [isDirty]);

  const handleDialogStay = useCallback(() => {
    setShowUnsavedDialog(false);
  }, []);

  const handleDialogLeave = useCallback(() => {
    setShowUnsavedDialog(false);
    setMode('view');
    setIsDirty(false);
    setPersonalInfoFormData({});
    setLocaleFormData({});
    setPendingPhoto(undefined);
  }, []);

  const handleEditClick = useCallback(() => {
    setMode('edit');
    setIsDirty(false);
    setPersonalInfoFormData({});
    setLocaleFormData({});
    setPendingPhoto(undefined);
  }, []);

  const handleLogout = useCallback(() => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  return {
    // Mode
    mode,
    isDirty,
    isSaving,
    showUnsavedDialog,
    canUpdate,
    clientId,

    // Data
    fullName,
    emailAddress,
    entityData,
    configuredTimezone,
    configuredLocale,
    timezoneOptions,
    localeOptions,
    lastUpdated,

    // Photo
    photoUrl,
    photoLoading,
    photoUploading,
    hasPhoto:
      pendingPhoto === undefined
        ? Boolean(photo?.fileId)
        : pendingPhoto !== null,

    // Work profile
    workProfileData,

    // View fields
    personalInfoViewFields,
    localeViewFields,

    // Refs
    personalInfoFormRef,
    localeFormRef,

    // Callbacks
    handleEditClick,
    handleCancel,
    handleSave,
    handleDialogStay,
    handleDialogLeave,
    handlePersonalInfoChange,
    handleLocaleChange,
    handlePhotoSelect,
    handlePhotoRemove,
    handleLogout,
  };
}
